import { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags } from '@lezer/highlight';

type HighlightRule = {
  tag: unknown;
  color?: string;
  fontStyle?: string;
  fontWeight?: string;
};

function isValidHighlightTag(tag: unknown): boolean {
  if (Array.isArray(tag)) {
    return tag.length > 0 && tag.every(isValidHighlightTag);
  }
  if (!tag || typeof tag !== 'object') return false;
  const candidate = tag as { id?: unknown; set?: unknown };
  if (typeof candidate.id !== 'number') return false;
  if (candidate.set === undefined) return true;
  if (!Array.isArray(candidate.set) || candidate.set.length === 0) return false;
  return candidate.set.every(
    (entry) =>
      !!entry && typeof entry === 'object' && typeof (entry as { id?: unknown }).id === 'number',
  );
}

function safeHighlightStyleDefine(rules: HighlightRule[]): Extension | null {
  const filteredRules = rules.filter((rule) => isValidHighlightTag(rule.tag));
  try {
    const style = HighlightStyle.define(filteredRules as any);
    // HighlightStyle is NOT a valid Extension on its own — wrap it with
    // syntaxHighlighting() so CodeMirror's extension set recognises it.
    return syntaxHighlighting(style);
  } catch (err) {
    console.warn(
      '[CodeMirror] Failed to build highlight style, disabling custom syntax colors.',
      err,
    );
    return null;
  }
}

/**
 * ============================================================================
 * CRITICAL: NEVER TOUCH THESE THEME COLORS FOR FUTURE DEVELOPMENT
 * ============================================================================
 * These CodeMirror editor themes have been carefully crafted for tasking.tech
 * to match the Monaco editor themes exactly.
 * Do NOT modify, remove, or override any colors in this file.
 * Any changes to syntax highlighting or editor colors must be approved.
 * ============================================================================
 */

// Tasking Dark Theme - GitHub Dark style
// Matches helpers/monacoThemes.ts taskingDarkTheme
export const taskingDarkTheme = EditorView.theme(
  {
    '&': {
      backgroundColor: '#0d1117',
      color: '#e6edf3',
    },
    '.cm-content': {
      backgroundColor: '#0d1117',
      color: '#e6edf3',
      caretColor: '#58a6ff',
    },
    '.cm-editor': {
      backgroundColor: '#0d1117',
    },
    '.cm-scroller': {
      backgroundColor: '#0d1117',
    },
    '.cm-gutters': {
      backgroundColor: '#0d1117',
      border: 'none',
    },
    '.cm-lineNumbers': {
      color: '#484f58',
    },
    '.cm-lineNumbers .cm-activeLineGutter': {
      color: '#e6edf3',
      backgroundColor: 'transparent',
    },
    '.cm-activeLine': {
      backgroundColor: '#161b22',
    },
    '.cm-selectionBackground': {
      backgroundColor: '#264f78',
    },
    '.cm-selectionMatch': {
      backgroundColor: '#264f7855',
    },
    '.cm-matchingBracket': {
      backgroundColor: '#3fb95033',
      outline: '1px solid #3fb950',
    },
    '.cm-nonmatchingBracket': {
      backgroundColor: 'transparent',
    },
    '.cm-indentGuide': {
      borderLeft: '1px solid #21262d',
    },
    '.cm-indentGuide.active': {
      borderLeft: '1px solid #30363d',
    },
    '.cm-scrollbar': {
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'var(--primary, #f97316)',
        borderRadius: '999px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'var(--primary, #fb923c)',
      },
      '&::-webkit-scrollbar-thumb:active': {
        backgroundColor: 'var(--primary, #ea580c)',
      },
    },
  },
  { dark: true },
);

// Tasking Light Theme - GitHub Light style
// Matches helpers/monacoThemes.ts taskingLightTheme
export const taskingLightTheme = EditorView.theme(
  {
    '&': {
      backgroundColor: '#ffffff',
      color: '#24292e',
    },
    '.cm-content': {
      backgroundColor: '#ffffff',
      color: '#24292e',
      caretColor: '#044289',
    },
    '.cm-editor': {
      backgroundColor: '#ffffff',
    },
    '.cm-scroller': {
      backgroundColor: '#ffffff',
    },
    '.cm-gutters': {
      backgroundColor: '#ffffff',
      border: 'none',
    },
    '.cm-lineNumbers': {
      color: '#959da5',
    },
    '.cm-lineNumbers .cm-activeLineGutter': {
      color: '#24292e',
      backgroundColor: 'transparent',
    },
    '.cm-activeLine': {
      backgroundColor: '#f6f8fa',
    },
    '.cm-selectionBackground': {
      backgroundColor: '#0366d625',
    },
    '.cm-selectionMatch': {
      backgroundColor: '#0366d615',
    },
    '.cm-matchingBracket': {
      backgroundColor: '#34d05833',
      outline: '1px solid #34d058',
    },
    '.cm-nonmatchingBracket': {
      backgroundColor: 'transparent',
    },
    '.cm-indentGuide': {
      borderLeft: '1px solid #eff2f5',
    },
    '.cm-indentGuide.active': {
      borderLeft: '1px solid #d7dbe0',
    },
    '.cm-scrollbar': {
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'var(--primary, #f97316)',
        borderRadius: '999px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'var(--primary, #fb923c)',
      },
      '&::-webkit-scrollbar-thumb:active': {
        backgroundColor: 'var(--primary, #ea580c)',
      },
    },
  },
  { dark: false },
);

// Syntax highlighting for Tasking Dark Theme
// Colors match helpers/monacoThemes.ts taskingDarkTheme rules
export const taskingDarkHighlightStyle = safeHighlightStyleDefine([
  // Comments - gray italic
  { tag: tags.comment, color: '#6A737D', fontStyle: 'italic' },
  { tag: tags.lineComment, color: '#6A737D', fontStyle: 'italic' },
  { tag: tags.blockComment, color: '#6A737D', fontStyle: 'italic' },

  // Strings - light blue
  { tag: tags.string, color: '#9ECBFF' },
  { tag: tags.escape, color: '#79B8FF' },
  { tag: tags.regexp, color: '#DBEDFF' },

  // Numbers - blue
  { tag: tags.number, color: '#79B8FF' },

  // Keywords - red/pink
  { tag: tags.keyword, color: '#FF7B72' },
  { tag: tags.controlKeyword, color: '#FF7B72' },
  { tag: tags.operatorKeyword, color: '#FF7B72' },

  // Types - orange
  { tag: tags.typeName, color: '#FFA657' },
  { tag: tags.className, color: '#FFA657' },
  { tag: tags.className, color: '#FFA657' },

  // Functions - purple
  { tag: tags.function(tags.variableName), color: '#D2A8FF' },
  { tag: tags.function(tags.propertyName), color: '#D2A8FF' },
  { tag: tags.definition(tags.function(tags.variableName)), color: '#D2A8FF' },

  // Variables
  { tag: tags.variableName, color: '#C9D1D9' },
  { tag: tags.variableName, color: '#FFA657' },
  { tag: tags.self, color: '#FF7B72' },
  { tag: tags.atom, color: '#79C0FF' },

  // Constants - blue
  { tag: tags.constant, color: '#79C0FF' },
  { tag: tags.bool, color: '#79C0FF' },

  // Operators
  { tag: tags.operator, color: '#FF7B72' },

  // Tags (HTML/JSX) - green
  { tag: tags.tagName, color: '#7EE787' },
  { tag: tags.attributeName, color: '#79C0FF' },
  { tag: tags.attributeValue, color: '#9ECBFF' },

  // Markdown
  { tag: tags.heading, color: '#79C0FF', fontWeight: 'bold' },
  { tag: tags.strong, fontWeight: 'bold' },
  { tag: tags.emphasis, fontStyle: 'italic' },
  { tag: tags.link, color: '#79C0FF' },
  { tag: tags.quote, color: '#7EE787', fontStyle: 'italic' },
  { tag: tags.list, color: '#FFA657' },

  // Default
  { tag: tags.name, color: '#C9D1D9' },
  { tag: tags.propertyName, color: '#79C0FF' },
]);

// Syntax highlighting for Tasking Light Theme
// Colors match helpers/monacoThemes.ts taskingLightTheme rules
export const taskingLightHighlightStyle = safeHighlightStyleDefine([
  // Comments - gray italic
  { tag: tags.comment, color: '#6E7781', fontStyle: 'italic' },
  { tag: tags.lineComment, color: '#6E7781', fontStyle: 'italic' },
  { tag: tags.blockComment, color: '#6E7781', fontStyle: 'italic' },

  // Strings - dark blue
  { tag: tags.string, color: '#0A3069' },
  { tag: tags.escape, color: '#0550AE' },
  { tag: tags.regexp, color: '#0A3069' },

  // Numbers - blue
  { tag: tags.number, color: '#0550AE' },

  // Keywords - red
  { tag: tags.keyword, color: '#CF222E' },
  { tag: tags.controlKeyword, color: '#CF222E' },
  { tag: tags.operatorKeyword, color: '#CF222E' },

  // Types - orange
  { tag: tags.typeName, color: '#953800' },
  { tag: tags.className, color: '#953800' },
  { tag: tags.className, color: '#953800' },

  // Functions - purple
  { tag: tags.function(tags.variableName), color: '#8250DF' },
  { tag: tags.function(tags.propertyName), color: '#8250DF' },
  { tag: tags.definition(tags.function(tags.variableName)), color: '#8250DF' },

  // Variables
  { tag: tags.variableName, color: '#24292F' },
  { tag: tags.variableName, color: '#953800' },
  { tag: tags.self, color: '#CF222E' },
  { tag: tags.atom, color: '#0550AE' },

  // Constants - blue
  { tag: tags.constant, color: '#0550AE' },
  { tag: tags.bool, color: '#0550AE' },

  // Operators
  { tag: tags.operator, color: '#CF222E' },

  // Tags (HTML/JSX) - green
  { tag: tags.tagName, color: '#116329' },
  { tag: tags.attributeName, color: '#0550AE' },
  { tag: tags.attributeValue, color: '#0A3069' },

  // Markdown
  { tag: tags.heading, color: '#0550AE', fontWeight: 'bold' },
  { tag: tags.strong, fontWeight: 'bold' },
  { tag: tags.emphasis, fontStyle: 'italic' },
  { tag: tags.link, color: '#0550AE' },
  { tag: tags.quote, color: '#116329', fontStyle: 'italic' },
  { tag: tags.list, color: '#953800' },

  // Default
  { tag: tags.name, color: '#24292F' },
  { tag: tags.propertyName, color: '#0550AE' },
]);

/**
 * Create a theme extension that supports CSS variable overrides for custom themes.
 * This allows the editor to respect custom CSS variables set by the theme system.
 */
export function createTaskingTheme(themeName: 'tasking-dark' | 'tasking-light'): Extension[] {
  const baseTheme = themeName === 'tasking-dark' ? taskingDarkTheme : taskingLightTheme;
  const highlightStyle =
    themeName === 'tasking-dark' ? taskingDarkHighlightStyle : taskingLightHighlightStyle;

  // Create a theme extension that uses CSS variables where possible
  // This allows custom themes to override colors via CSS variables
  const cssVariableTheme = EditorView.theme(
    {
      '&': {
        backgroundColor:
          'var(--editor-background, ' +
          (themeName === 'tasking-dark' ? '#0d1117' : '#ffffff') +
          ')',
        color:
          'var(--editor-foreground, ' +
          (themeName === 'tasking-dark' ? '#e6edf3' : '#24292e') +
          ')',
      },
      '.cm-content': {
        backgroundColor:
          'var(--editor-background, ' +
          (themeName === 'tasking-dark' ? '#0d1117' : '#ffffff') +
          ')',
        color:
          'var(--editor-foreground, ' +
          (themeName === 'tasking-dark' ? '#e6edf3' : '#24292e') +
          ')',
        caretColor:
          'var(--editor-cursor, ' + (themeName === 'tasking-dark' ? '#58a6ff' : '#044289') + ')',
      },
      '.cm-gutters': {
        backgroundColor:
          'var(--editor-gutter-background, ' +
          (themeName === 'tasking-dark' ? '#0d1117' : '#ffffff') +
          ')',
      },
      '.cm-lineNumbers': {
        color:
          'var(--editor-line-number, ' +
          (themeName === 'tasking-dark' ? '#484f58' : '#959da5') +
          ')',
      },
      '.cm-lineNumbers .cm-activeLineGutter': {
        color:
          'var(--editor-line-number-active, ' +
          (themeName === 'tasking-dark' ? '#e6edf3' : '#24292e') +
          ')',
      },
      '.cm-activeLine': {
        backgroundColor:
          'var(--editor-line-highlight, ' +
          (themeName === 'tasking-dark' ? '#161b22' : '#f6f8fa') +
          ')',
      },
      '.cm-selectionBackground': {
        backgroundColor:
          'var(--editor-selection, ' +
          (themeName === 'tasking-dark' ? '#264f78' : '#0366d625') +
          ')',
      },
    },
    { dark: themeName === 'tasking-dark' },
  );

  const extensions: Extension[] = [baseTheme, cssVariableTheme];
  if (highlightStyle) extensions.push(highlightStyle);
  return extensions;
}

/**
 * ============================================================================
 * END OF THEME DEFINITIONS - DO NOT ADD OR MODIFY COLORS ABOVE THIS LINE
 * ============================================================================
 */
